import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { getDriver } from '../../services/driverService';
import { Driver, DriverStatus } from '../../types/driver';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Edit, ArrowBack } from '@mui/icons-material';

const DriverDetail: React.FC = () => {
  const { t } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [driver, setDriver] = useState<Driver | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDriverData = async () => {
      if (!id) return;
      
      try {
        const driverData = await getDriver(id);
        setDriver(driverData);
      } catch (error) {
        console.error('Error fetching driver details:', error);
        navigate('/drivers');
      } finally {
        setLoading(false);
      }
    };

    fetchDriverData();
  }, [id, navigate]);

  const getStatusColor = (status: DriverStatus) => {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Inactive':
        return 'default';
      case 'On Delivery':
        return 'primary';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (!driver) {
    return (
      <Box textAlign="center" p={4}>
        <Typography variant="h6">{t('drivers.notFound')}</Typography>
        <Button onClick={() => navigate('/drivers')} sx={{ mt: 2 }}>
          {t('backToDrivers')}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/drivers')} sx={{ mr: 1 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1">
          {driver.name}
        </Typography>
        <Box flexGrow={1} />
        <Button
          variant="contained"
          color="primary"
          startIcon={<Edit />}
          onClick={() => navigate(`/drivers/edit/${driver.id}`)}
        >
          {t('edit')}
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('drivers.driverInfo')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t('email')}
                  </Typography>
                  <Typography>{driver.email}</Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t('phone')}
                  </Typography>
                  <Typography>{driver.phone || '-'}</Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t('drivers.status')}
                  </Typography>
                  <Chip 
                    label={t(`drivers.statuses.${driver.status}`)}
                    color={getStatusColor(driver.status) as any}
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t('drivers.vehicleType')}
                  </Typography>
                  <Typography>
                    {driver.vehicleType ? t(`drivers.vehicleTypes.${driver.vehicleType}`) : '-'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t('drivers.vehicleNumber')}
                  </Typography>
                  <Typography>{driver.vehicleNumber || '-'}</Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t('createdAt')}
                  </Typography>
                  <Typography>
                    {new Date(driver.createdAt).toLocaleDateString()}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Additional sections can be added here, such as recent deliveries, stats, etc. */}
        <Grid xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              {t('drivers.activity')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography color="textSecondary">
              {t('drivers.noActivity')}
            </Typography>
            {/* Activity timeline or other relevant information can be added here */}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DriverDetail;
